<!-- eslint-disable vue/no-mutating-props -->
<!-- 商品、分类、套餐、加料与库存的展示。数据请求和编辑弹窗由父页面处理。 -->
<template>
  <section class="product-panel">
    <div v-if="stats.length" class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div v-if="section === 'products'" class="filter-bar">
        <el-input v-model.trim="productFilters.keyword" clearable placeholder="搜索商品名称" class="filter-control" @keyup.enter="emit('searchProducts')" />
        <div class="filter-row">
          <el-select v-model="productFilters.categoryId" clearable placeholder="商品分类" class="filter-control">
            <el-option v-for="category in categories" :key="category.id" :label="category.name" :value="category.id" />
          </el-select>
          <el-select v-model="productFilters.status" clearable placeholder="销售状态" class="filter-control">
            <el-option label="在售" value="ON" />
            <el-option label="下架" value="OFF" />
          </el-select>
        </div>
        <div class="filter-bar__actions">
          <button class="search-button" @click="emit('searchProducts')">搜索</button>
          <button class="reset-button" @click="emit('resetProducts')">重置</button>
        </div>
      </div>

      <div v-else-if="section === 'combos'" class="filter-bar">
        <div class="filter-bar__meta">
          <span>商品套餐支持真实查询与新增、编辑，套餐明细会随表单一并保存。</span>
        </div>
      </div>

      <div v-else-if="section === 'inventory'" class="filter-bar">
        <div class="filter-bar__meta">
          <span>库存按 SKU 维度维护，调整后会立即回写后端。</span>
        </div>
      </div>

      <div v-if="section === 'products'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>商品名称</th>
              <th>分类</th>
              <th class="is-right">售价</th>
              <th class="is-right">库存</th>
              <th class="is-right">销量</th>
              <th>外卖</th>
              <th>销售状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in products" :key="product.id">
              <td>
                <div class="cell-stack">
                  <span class="cell-text">{{ product.name }}</span>
                  <small>{{ product.subtitle || product.id }}</small>
                </div>
              </td>
              <td><span class="cell-text">{{ product.categoryName || product.categoryId }}</span></td>
              <td class="is-right">{{ formatAmount(product.price) }}</td>
              <td class="is-right">{{ product.stock ?? '--' }}</td>
              <td class="is-right">{{ product.sales ?? 0 }}</td>
              <td>
                <span :class="['status-tag', toneClass(product.supportsDelivery === false ? 'muted' : 'success')]">
                  {{ product.supportsDelivery === false ? '仅自取' : '支持外卖' }}
                </span>
              </td>
              <td><span :class="['status-tag', toneClass(productStatusTone(product.status))]">{{ getProductStatusText(product.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑商品" @click="emit('editProduct', product)"><el-icon><EditPen /></el-icon></button>
                  <button class="is-danger" title="删除商品" @click="emit('removeProduct', product)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!products.length && !loadingProducts"><td colspan="8"><el-empty description="暂无商品数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'combos'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>套餐名称</th>
              <th>门店</th>
              <th class="is-right">原价</th>
              <th class="is-right">售价</th>
              <th class="is-right">商品数</th>
              <th>状态</th>
              <th>创建时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="combo in combos" :key="combo.id">
              <td>
                <div class="cell-stack">
                  <span class="cell-text">{{ combo.name }}</span>
                  <small>{{ combo.items?.map((item) => `${item.productName || item.productId} × ${item.quantity ?? 1}`).join('、') || '暂无明细' }}</small>
                </div>
              </td>
              <td><span class="cell-text">{{ combo.storeId || '--' }}</span></td>
              <td class="is-right">{{ formatAmount(combo.originalPrice ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(combo.salePrice ?? 0) }}</td>
              <td class="is-right">{{ combo.items?.length ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(comboStatusTone(combo.status))]">{{ getComboStatusText(combo.status) }}</span></td>
              <td>{{ combo.createdAt || '--' }}</td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑套餐" @click="emit('editCombo', combo)"><el-icon><EditPen /></el-icon></button>
                  <button class="is-danger" title="删除套餐" @click="emit('removeCombo', combo)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!combos.length && !loadingCombos"><td colspan="8"><el-empty description="暂无套餐数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'categories'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>分类名称</th>
              <th>菜单标识</th>
              <th>所选门店</th>
              <th class="is-right">排序</th>
              <th>外卖</th>
              <th>状态</th>
              <th>创建时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="category in categories" :key="category.id">
              <td><span class="cell-text">{{ category.name }}</span></td>
              <td><span class="cell-text">{{ menuTabLabel }}</span></td>
              <td><span class="cell-text">{{ formatCategoryStores(category) }}</span></td>
              <td class="is-right">{{ category.sort ?? 0 }}</td>
              <td>
                <span :class="['status-tag', toneClass(category.supportsDelivery === false ? 'muted' : 'success')]">
                  {{ category.supportsDelivery === false ? '仅自取' : '支持外卖' }}
                </span>
              </td>
              <td><span :class="['status-tag', toneClass(categoryStatusTone(category.status))]">{{ getCategoryStatusText(category.status) }}</span></td>
              <td>{{ category.createdAt || '--' }}</td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑分类" @click="emit('editCategory', category)"><el-icon><EditPen /></el-icon></button>
                  <button class="is-danger" title="删除分类" @click="emit('removeCategory', category)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!categories.length && !loadingCategories"><td colspan="8"><el-empty description="暂无分类数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'extras'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>加料名称</th>
              <th class="is-right">加价</th>
              <th>推荐</th>
              <th class="is-right">排序</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="extra in extras" :key="extra.id">
              <td><span class="cell-text">{{ extra.name }}</span></td>
              <td class="is-right">{{ formatAmount(extra.price ?? 0) }}</td>
              <td>{{ extra.recommended ? '推荐' : '普通' }}</td>
              <td class="is-right">{{ extra.sort ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(extraStatusTone(extra.status))]">{{ getExtraStatusText(extra.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑加料" @click="emit('editExtra', extra)"><el-icon><EditPen /></el-icon></button>
                  <button class="is-danger" title="删除加料" @click="emit('removeExtra', extra)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!extras.length && !loadingExtras"><td colspan="6"><el-empty description="暂无加料数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>商品</th>
              <th>SKU</th>
              <th>门店</th>
              <th class="is-right">当前库存</th>
              <th class="is-right">安全库存</th>
              <th>库存状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inventory in inventories" :key="inventory.skuId + inventory.storeId">
              <td><span class="cell-text">{{ inventory.productName || inventory.productId }}</span></td>
              <td><span class="cell-text">{{ inventory.skuId }}</span></td>
              <td><span class="cell-text">{{ inventory.storeId || '--' }}</span></td>
              <td class="is-right">{{ inventory.stock }}</td>
              <td class="is-right">{{ inventory.safetyStock ?? '--' }}</td>
              <td><span :class="['status-tag', toneClass(inventoryStatusTone(inventory))]">{{ getInventoryStatusText(inventory) }}</span></td>
              <td class="is-right"><div class="row-actions"><button title="调整库存" @click="emit('editInventory', inventory)"><el-icon><EditPen /></el-icon></button></div></td>
            </tr>
            <tr v-if="!inventories.length && !loadingInventory"><td colspan="7"><el-empty description="暂无库存数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="section === 'products' && productPage" class="table-footer">
        <span>共 {{ productPage.total }} 条记录，第 {{ productPage.page }}/{{ productTotalPages }} 页</span>
        <div>
          <button :disabled="productPage.page <= 1" @click="emit('changeProductPage', productPage.page - 1)">上一页</button>
          <button class="is-active">{{ productPage.page }}</button>
          <button :disabled="!productPage.hasMore" @click="emit('changeProductPage', productPage.page + 1)">下一页</button>
        </div>
      </div>

      <div v-else-if="section === 'inventory' && inventoryPage" class="table-footer">
        <span>共 {{ inventoryPage.total }} 条记录，第 {{ inventoryPage.page }}/{{ inventoryTotalPages }} 页</span>
        <div>
          <button :disabled="inventoryPage.page <= 1" @click="emit('changeInventoryPage', inventoryPage.page - 1)">上一页</button>
          <button class="is-active">{{ inventoryPage.page }}</button>
          <button :disabled="!inventoryPage.hasMore" @click="emit('changeInventoryPage', inventoryPage.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import { Delete, EditPen } from '@element-plus/icons-vue'
import type { PageResult } from '@jiaofa/api-types'
import type { AdminCategory, AdminCombo, AdminExtra, AdminInventory, AdminProduct, ProductListParams } from '@/api/product'
import type { AdminStore } from '@/api/store'
import { formatStoreNames } from '@/utils/store-names'
import { MENU_TAB_LABELS, MenuTab } from '@jiaofa/constants'
import type { OperationStat } from '@/data/operationDesign'

/** 分类只归属「经典菜单」，标识固定不可修改，列表统一展示名称 */
const menuTabLabel = MENU_TAB_LABELS[MenuTab.Classic]

type ProductSection = 'products' | 'categories' | 'combos' | 'extras' | 'inventory'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: ProductSection
  stats: OperationStat[]
  products: AdminProduct[]
  productPage?: PageResult<AdminProduct>
  productFilters: ProductListParams
  categories: AdminCategory[]
  storeOptions: AdminStore[]
  combos: AdminCombo[]
  extras: AdminExtra[]
  inventories: AdminInventory[]
  inventoryPage?: PageResult<AdminInventory>
  loadingProducts: boolean
  loadingCategories: boolean
  loadingCombos: boolean
  loadingExtras: boolean
  loadingInventory: boolean
  productTotalPages: number
  inventoryTotalPages: number
  formatAmount: (amountInFen: number) => string
  getProductStatusText: (status: string) => string
  getCategoryStatusText: (status: string) => string
  getComboStatusText: (status?: string) => string
  getExtraStatusText: (status: string) => string
  getInventoryStatusText: (inventory: AdminInventory) => string
  productStatusTone: (status: string) => StatusTone
  categoryStatusTone: (status: string) => StatusTone
  comboStatusTone: (status?: string) => StatusTone
  extraStatusTone: (status: string) => StatusTone
  inventoryStatusTone: (inventory: AdminInventory) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  searchProducts: []
  resetProducts: []
  changeProductPage: [page: number]
  changeInventoryPage: [page: number]
  editProduct: [item: AdminProduct]
  editCategory: [item: AdminCategory]
  editCombo: [item: AdminCombo]
  editExtra: [item: AdminExtra]
  editInventory: [item: AdminInventory]
  removeProduct: [item: AdminProduct]
  removeCategory: [item: AdminCategory]
  removeCombo: [item: AdminCombo]
  removeExtra: [item: AdminExtra]
}>()

const {
  section,
  stats,
  products,
  productPage,
  productFilters,
  categories,
  storeOptions,
  combos,
  extras,
  inventories,
  inventoryPage,
  loadingProducts,
  loadingCategories,
  loadingCombos,
  loadingExtras,
  loadingInventory,
  productTotalPages,
  inventoryTotalPages,
  formatAmount,
  getProductStatusText,
  getCategoryStatusText,
  getComboStatusText,
  getExtraStatusText,
  getInventoryStatusText,
  productStatusTone,
  categoryStatusTone,
  comboStatusTone,
  extraStatusTone,
  inventoryStatusTone,
  toneClass,
} = toRefs(props)

function parseStoreIds(category: AdminCategory): string[] {
  if (Array.isArray(category.storeIds) && category.storeIds.length) {
    return category.storeIds.map(String).map((item) => item.trim()).filter(Boolean)
  }
  if (!category.storeId) return []
  return category.storeId
    .split(/[\n,，]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function formatCategoryStores(category: AdminCategory): string {
  // 优先后端同序返回的 storeNames（自定义/已删除门店也能给出名字），本地门店表只作为回退
  const fallbackNames = new Map(storeOptions.value.map((store) => [store.id, store.name]))
  return formatStoreNames(parseStoreIds(category), category.storeNames, fallbackNames)
}
</script>

<style scoped lang="scss">
.product-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-control { width: 220px; }
.filter-row { display: flex; gap: 12px; }
.filter-row .filter-control { min-width: 124px; }
.filter-bar__actions { display: flex; gap: 8px; margin-left: auto; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.cell-stack { display: flex; flex-direction: column; gap: 4px; }
.cell-stack small { color: var(--jf-color-text-secondary); font-size: 12px; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button.is-danger { color: var(--jf-color-error); }
.row-actions button.is-danger:hover { background: rgba(231, 101, 101, .12); }
.row-actions button:disabled, .table-footer button:disabled { cursor: not-allowed; opacity: .5; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
